import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { 
  saveReflection, 
  getTodayReflection, 
  getDayMode,
  getReflections 
} from '../utils/localStorage';
import { getDayModeConfig } from '../utils/dayModes';
import { 
  Moon, 
  Sparkles, 
  CloudRain, 
  Sun,
  ChevronRight,
  Calendar,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';
import VoiceInput from './VoiceInput';

const GentleReview = () => {
  const [step, setStep] = useState(0);
  const [hardThings, setHardThings] = useState('');
  const [accomplishments, setAccomplishments] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [todayReflection, setTodayReflection] = useState(null);
  const [pastReflections, setPastReflections] = useState([]);
  
  const dayMode = getDayMode();
  const modeConfig = dayMode ? getDayModeConfig(dayMode) : null;
  
  useEffect(() => {
    const existing = getTodayReflection();
    if (existing) {
      setTodayReflection(existing);
      setHardThings(existing.hardThings || '');
      setAccomplishments(existing.accomplishments || '');
      setIsComplete(true);
    }
    setPastReflections(getReflections().slice(0, 7));
  }, []);
  
  const handleSave = () => {
    const reflection = saveReflection({
      hardThings: hardThings.trim(),
      accomplishments: accomplishments.trim(),
    });
    setTodayReflection(reflection);
    setIsComplete(true);
    toast.success('Your day is closed. Rest well.');
  };
  
  const steps = [
    {
      id: 'hard',
      title: 'What was hard today?',
      subtitle: 'Acknowledge the weight you carried',
      icon: CloudRain,
      placeholder: 'The kids were extra energetic... I felt overwhelmed by...',
    },
    {
      id: 'done',
      title: 'What did you actually do?',
      subtitle: 'Everything counts, even the small things',
      icon: Sun,
      placeholder: 'Made breakfast... Helped with homework... Did a load of laundry...',
    },
  ];
  
  if (isComplete) {
    return (
      <div className="space-y-6 relative" data-testid="gentle-review-complete">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 text-3xl opacity-20 pointer-events-none">🌙</div>
        
        {/* Completion card */}
        <Card className="p-8 bg-good-bg border-good-border text-center shadow-lifted relative overflow-hidden">
          {/* Decorative stars */}
          <div className="absolute top-4 left-4 text-xl opacity-30">✨</div>
          <div className="absolute bottom-4 right-4 text-xl opacity-30">🌿</div>
          
          <div className="w-16 h-16 rounded-full bg-white mx-auto mb-4 flex items-center justify-center shadow-paper">
            <Sparkles className="w-8 h-8 text-good-text" />
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-good-text mb-2">
            You did enough today.
          </h2>
          <p className="text-good-text/80 mb-4 font-accent italic">
            Your day is closed. Whatever wasn't done can wait.
          </p>
          
          {todayReflection && (
            <div className="mt-6 text-left space-y-4">
              {todayReflection.hardThings && (
                <div className="p-4 bg-white/50 rounded-lg journal-entry">
                  <p className="text-sm text-good-text/60 mb-1 font-accent">What was hard:</p>
                  <p className="text-good-text font-handwritten text-lg">{todayReflection.hardThings}</p>
                </div>
              )}
              {todayReflection.accomplishments && (
                <div className="p-4 bg-white/50 rounded-lg journal-entry">
                  <p className="text-sm text-good-text/60 mb-1 font-accent">What you did:</p>
                  <p className="text-good-text font-handwritten text-lg">{todayReflection.accomplishments}</p>
                </div>
              )}
            </div>
          )}
        </Card>
        
        {/* Edit button */}
        <Button
          data-testid="gentle-review-edit"
          onClick={() => {
            setIsComplete(false);
            setStep(0);
          }}
          variant="outline"
          className="w-full rounded-lg border-stone-300 text-ink-700"
        >
          Edit today's reflection
        </Button>
        
        {/* Past reflections */}
        {pastReflections.length > 1 && (
          <div className="space-y-4">
            <h3 className="font-serif text-xl text-ink-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brown" />
              Recent days
            </h3>
            <div className="space-y-3">
              {pastReflections.slice(1).map((reflection, idx) => {
                const refMode = reflection.dayMode ? getDayModeConfig(reflection.dayMode) : null;
                return (
                  <Card 
                    key={idx} 
                    className="p-4 bg-paper-light shadow-lifted border border-stone-200"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      {refMode && <span className="text-lg">{refMode.emoji}</span>}
                      <span className="text-sm text-ink-500 font-accent italic">
                        {new Date(reflection.date).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                    {reflection.accomplishments && (
                      <p className="text-sm text-ink-900 font-handwritten text-base line-clamp-2">
                        {reflection.accomplishments}
                      </p>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  const currentStep = steps[step];
  const StepIcon = currentStep.icon;
  
  return (
    <div className="space-y-6 relative" data-testid="gentle-review-page">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 text-3xl opacity-20 pointer-events-none">🌙</div>
      
      {/* Header */}
      <div className="text-center">
        <div className="w-14 h-14 rounded-full bg-brown/10 mx-auto mb-4 flex items-center justify-center border border-brown/20">
          <Moon className="w-7 h-7 text-brown" />
        </div>
        <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-2">
          Gentle Review
        </h2>
        <p className="text-ink-500 font-accent italic">
          Close your day without judgment 🌿
        </p>
        
        {modeConfig && (
          <div className={`
            inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg border
            ${modeConfig.bgColor} ${modeConfig.borderColor} ${modeConfig.textColor}
          `}>
            <span>{modeConfig.emoji}</span>
            <span className="text-sm font-serif">{modeConfig.name}</span>
          </div>
        )}
      </div>
      
      {/* Progress */}
      <div className="flex gap-2">
        {steps.map((s, idx) => (
          <div
            key={s.id}
            className={`
              h-1 flex-1 rounded-full transition-all
              ${idx <= step ? 'bg-brown' : 'bg-stone-300'}
            `}
          />
        ))}
      </div>
      
      {/* Current step */}
      <Card className="p-6 bg-paper-light shadow-lifted border border-stone-200 animate-fade-in journal-lines relative">
        {/* Decorative washi tape */}
        <div className="absolute -top-2 left-12 w-16 h-5 bg-sage/40 transform -rotate-1 rounded-sm" />
        
        <div className="flex items-center gap-3 mb-4 pt-2">
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center
            ${step === 0 ? 'bg-survival-bg' : 'bg-good-bg'}
          `}>
            <StepIcon className={`w-5 h-5 ${step === 0 ? 'text-survival-text' : 'text-good-text'}`} />
          </div>
          <div>
            <h3 className="font-serif text-lg text-ink-900">{currentStep.title}</h3>
            <p className="text-sm text-ink-500 font-accent italic">{currentStep.subtitle}</p>
          </div>
        </div>
        
        <div className="flex items-center justify-end mb-1">
          <VoiceInput onTranscription={(text) => {
            if (step === 0) setHardThings(prev => prev ? prev + ' ' + text : text);
            else setAccomplishments(prev => prev ? prev + ' ' + text : text);
          }} />
        </div>
        <Textarea
          data-testid={`gentle-review-input-${currentStep.id}`}
          value={step === 0 ? hardThings : accomplishments}
          onChange={(e) => step === 0 ? setHardThings(e.target.value) : setAccomplishments(e.target.value)}
          placeholder={currentStep.placeholder}
          className="min-h-[150px] border-stone-300 focus:border-brown focus:ring-brown/20 mb-4 font-handwritten text-lg bg-transparent"
        />
        
        <div className="flex gap-3">
          {step > 0 && (
            <Button
              data-testid="gentle-review-back"
              onClick={() => setStep(step - 1)}
              variant="outline"
              className="rounded-lg border-stone-300"
            >
              Back
            </Button>
          )}
          
          {step < steps.length - 1 ? (
            <Button
              data-testid="gentle-review-next"
              onClick={() => setStep(step + 1)}
              className="flex-1 vintage-btn btn-brown rounded-lg"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              data-testid="gentle-review-complete"
              onClick={handleSave}
              className="flex-1 vintage-btn btn-sage rounded-lg"
            >
              <Heart className="w-4 h-4 mr-2" />
              Close my day
            </Button>
          )}
        </div>
      </Card>
      
      {/* Skip option */}
      <div className="text-center">
        <Button
          data-testid="gentle-review-skip"
          onClick={handleSave}
          variant="ghost"
          className="text-ink-500 hover:text-ink-700 font-accent italic"
        >
          Skip and close day
        </Button>
      </div>
    </div>
  );
};

export default GentleReview;
